// frontend/src/AdminApp.js - Modern Admin Panel with improved UX
import React, { useState, useEffect } from 'react';
import AdminQuickBooking from './components/AdminQuickBooking';
import AdminBulkBooking from './components/AdminBulkBooking';
import AdminManageBookings from './components/AdminManageBookings';
import AdminManageCourts from './components/AdminManageCourts';
import AdminManageSports from './components/AdminManageSports';
import AdminManagePricing from './components/AdminManagePricing';
import AdminPlatformConfig from './components/AdminPlatformConfig';
import AdminManageUsers from './components/AdminManageUsers';
import AdminBrandingSettings from './components/AdminBrandingSettings';
import AdminReconciliation from './components/AdminReconciliation';
import AdminCancellations from './components/AdminCancellations';
import AdminComprehensiveReport from './components/AdminComprehensiveReport';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandingProvider, useBranding } from './contexts/BrandingContext';
import './components/AdminApp.css';

const allTabs = [
  { id: 'quick-booking', label: 'Quick Booking', icon: 'M13 10V3L4 14h7v7l9-11h-7z', permission: 'manage_bookings' },
  { id: 'bulk-booking', label: 'Bulk Booking', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', permission: 'bulk_booking' },
  { id: 'manage-bookings', label: 'Bookings', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', permission: 'manage_bookings' },
  { id: 'manage-courts', label: 'Courts', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', permission: 'manage_courts' },
  { id: 'manage-sports', label: 'Sports', icon: 'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z', permission: 'manage_sports' },
  { id: 'manage-pricing', label: 'Pricing', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', permission: 'manage_pricing' },
  { id: 'manage-users', label: 'Users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', superAdminOnly: true },
  { id: 'platform-config', label: 'Platform', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z', superAdminOnly: true },
  { id: 'reconciliation', label: 'Reconciliation', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
  { id: 'cancellations', label: 'Cancellations', icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636', adminOnly: true },
  { id: 'branding', label: 'Branding', icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01', superAdminOnly: true },
  { id: 'reports', label: 'Reports', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', permission: 'view_reports' }
];

function AdminApp() {
  const { isAuthenticated, logout, hasPermission, isSuperAdmin, user } = useAuth();
  const { branding, refetchBranding } = useBranding();
  const [activeTab, setActiveTab] = useState('quick-booking');

  // Track previous tab to refetch branding only when leaving branding tab
  const [prevTab, setPrevTab] = useState(activeTab);

  // Filter tabs based on permissions
  const tabs = allTabs.filter(tab => {
    if (tab.superAdminOnly) return isSuperAdmin();
    if (tab.adminOnly) return hasPermission('manage_bookings'); // Admin+ can cancel
    if (tab.permission) return hasPermission(tab.permission);
    return true; // No permission required (like Reconciliation)
  });

  useEffect(() => {
    // Only refetch branding when leaving the branding tab (after user may have made changes)
    if (prevTab === 'branding' && activeTab !== 'branding') {
      refetchBranding();
    }
    setPrevTab(activeTab);
  }, [activeTab, prevTab, refetchBranding]);

  // Reset to first available tab if current tab becomes unavailable
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'quick-booking': return <AdminQuickBooking />;
      case 'bulk-booking': return <AdminBulkBooking />;
      case 'manage-bookings': return <AdminManageBookings />;
      case 'manage-courts': return <AdminManageCourts />;
      case 'manage-sports': return <AdminManageSports />;
      case 'manage-pricing': return <AdminManagePricing />;
      case 'manage-users': return <AdminManageUsers />;
      case 'platform-config': return <AdminPlatformConfig />;
      case 'reconciliation': return <AdminReconciliation />;
      case 'cancellations': return <AdminCancellations userRole={user?.role} userName={user?.name || user?.username} />;
      case 'branding': return <AdminBrandingSettings />;
      case 'reports': return <AdminComprehensiveReport />;
      default: return <AdminQuickBooking />;
    }
  };

  // Calculate hover color (slightly darker version of primary)
  const primaryHover = branding.primaryColor ?
    `color-mix(in srgb, ${branding.primaryColor} 85%, black)` : '#5a6fd6';
  const primaryLight = branding.primaryColor ?
    `color-mix(in srgb, ${branding.primaryColor} 15%, transparent)` : 'rgba(102, 126, 234, 0.15)';

  return (
    <div
      className="admin-container"
      style={{
        '--primary-color': branding.primaryColor,
        '--primary-hover': primaryHover,
        '--primary-light': primaryLight
      }}
    >
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.centerName} className="sidebar-logo" />
          ) : (
            <div className="sidebar-logo-placeholder" style={{ backgroundColor: branding.primaryColor }}>
              {branding.centerName.charAt(0)}
            </div>
          )}
          <div className="sidebar-brand-info">
            <h2>{branding.centerName}</h2>
            {branding.tagline && <p className="sidebar-tagline">{branding.tagline}</p>}
          </div>
        </div>

        <nav className="sidebar-nav">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
            >
              <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
              </svg>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          {/* User Profile */}
          <div className="user-profile">
            <div className="user-avatar" style={{ backgroundColor: branding.primaryColor }}>
              {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </div>
            <div className="user-info">
              <span className="user-name">{user?.first_name || user?.email?.split('@')[0] || 'User'}</span>
              <span className="user-role">{user?.role?.replace('_', ' ') || 'Staff'}</span>
            </div>
          </div>
          <button onClick={logout} className="logout-btn">
            <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <h1>{tabs.find(t => t.id === activeTab)?.label}</h1>
        </header>
        <div className="admin-content">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

function AdminLogin() {
  const { login } = useAuth();
  const { branding } = useBranding();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(credentials.username, credentials.password);
      if (!result.success) {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.centerName} className="login-logo" />
          ) : (
            <div className="login-logo-placeholder" style={{ backgroundColor: branding.primaryColor }}>
              {branding.centerName?.charAt(0) || 'S'}
            </div>
          )}
          <h2>{branding.centerName || 'Admin Login'}</h2>
          <p>Sign in to access the admin panel</p>
        </div>

        {error && (
          <div className="login-error">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label>Email</label>
            <input
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              placeholder="Enter your password"
              required
            />
          </div>

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

      </div>
    </div>
  );
}

export default function AdminAppWrapper() {
  return (
    <AuthProvider>
      <BrandingProvider>
        <AdminApp />
      </BrandingProvider>
    </AuthProvider>
  );
}
